import './css/app.css';
import {
  getState, subscribe, startMockRuntime, getRoles,
  setActiveHouse, addHouse, MAX_HOUSES, reasonMessage,
} from './js/mockStore.js';
import { t } from './js/i18n.js';
import { renderOverview } from './js/views/overview.js';
import { renderEnvironment } from './js/views/environment.js';
import { renderActuators } from './js/views/actuators.js';
import { renderWater } from './js/views/water.js';
import { renderCam } from './js/views/cam.js';
import { renderSettings } from './js/views/settings.js';
import { renderScene3d, destroyScene3d } from './js/views/scene3d.js';

/** Sections stacked on each greenhouse home (order). */
const SECTIONS = [
  { id: 'overview', key: 'overview' },
  { id: 'env', key: 'env' },
  { id: 'actuators', key: 'actuators' },
  { id: 'water', key: 'water' },
  { id: 'cam', key: 'cam' },
  { id: 'scene3d', key: 'scene3d' },
  { id: 'settings', key: 'settings' },
];

/** Virtualized house-tab window (DOM nodes only — not all houses). */
const TAB_WINDOW = 24;

let lastSig = '';
let lastNavSig = '';
let renderScheduled = false;
let lastActiveHouseId = '';
let tabWindowStart = 0;
let houseJumpFilter = '';

function canControlFrom(state) {
  const role = getRoles().find((r) => r.id === state.role);
  return !!(role && role.canControl);
}

function controlSig(state) {
  return JSON.stringify({
    house: state.activeHouseId,
    role: state.role,
    lang: state.lang,
    mode: state.mode,
    controlMode: state.controlMode,
    manualHoldUntil: state.manualHoldUntil,
    windows: state.windows,
    curtain: state.curtain,
    lights: state.lights,
    fans: state.fans,
    ac: state.ac,
    water: state.water,
    cameras: state.cameras,
    plantProfileId: state.plantProfileId,
    alarms: state.alarms.map((a) => [a.id, a.cleared]),
    hw: state.hardwareLocks,
    power: state.power,
    selectedHotspot: state.selectedHotspot,
    logLen: state.overrideLog.length,
    wind: state.env.windMs,
    co2: state.env.co2Ppm,
    houseCount: state.houseCount,
  });
}

function navSig(state) {
  return JSON.stringify({
    active: state.activeHouseId,
    count: state.houseCount,
    lang: state.lang,
    codes: state.houseList.map((h) => h.id + ':' + h.code),
    jump: houseJumpFilter,
    tabStart: tabWindowStart,
  });
}

function ensureHome(main, houseId) {
  const needRebuild = main.dataset.layout !== 'home-all'
    || main.dataset.houseId !== houseId;
  if (!needRebuild) return false;
  destroyScene3d();
  main.dataset.layout = 'home-all';
  main.dataset.houseId = houseId;
  main.classList.add('home-all');
  main.innerHTML = `
    <div class="home-stack">
      ${SECTIONS.map((v) =>
        `<section id="section-${v.id}" class="home-section" data-section="${v.id}"></section>`
      ).join('')}
    </div>
  `;
  return true;
}

function visibleHouseSlice(list) {
  let filtered = list;
  const q = houseJumpFilter.trim().toLowerCase();
  if (q) {
    filtered = list.filter((h) =>
      h.code.toLowerCase().includes(q)
      || h.name.toLowerCase().includes(q)
      || h.id.toLowerCase().includes(q));
  }
  const start = Math.max(0, Math.min(tabWindowStart, Math.max(0, filtered.length - TAB_WINDOW)));
  tabWindowStart = start;
  const end = Math.min(filtered.length, start + TAB_WINDOW);
  return { filtered, start, end, slice: filtered.slice(start, end) };
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

function renderHouseTabs(nav, state) {
  const { filtered, start, end, slice } = visibleHouseSlice(state.houseList);
  const atCap = state.houseCount >= MAX_HOUSES;
  const jumpEl = nav.querySelector('[data-house-jump]');
  const jumpFocused = document.activeElement === jumpEl;
  const selStart = jumpFocused ? jumpEl.selectionStart : null;
  const selEnd = jumpFocused ? jumpEl.selectionEnd : null;

  nav.innerHTML = `
    <div class="house-nav-tools">
      <input type="search" class="house-jump" data-house-jump
        placeholder="${t(state.lang, 'houseJump')}"
        value="${escapeAttr(houseJumpFilter)}"
        aria-label="${t(state.lang, 'houseJump')}" />
      <span class="badge" data-house-count>${state.houseCount}/${MAX_HOUSES}</span>
      <button type="button" class="nav-btn house-add" data-house-add title="${t(state.lang, 'addHouse')}"
        ${atCap ? 'disabled' : ''}>＋</button>
    </div>
    <div class="house-tabs-wrap" data-house-tabs-wrap>
      ${start > 0 ? `<button type="button" class="nav-btn house-more" data-tab-prev aria-label="prev">‹</button>` : ''}
      <div class="house-tabs" data-house-tabs>
        ${slice.map((h) => `
          <button type="button" class="nav-btn house-tab ${h.id === state.activeHouseId ? 'active' : ''}"
            data-house-id="${escapeAttr(h.id)}" title="${escapeAttr(h.name)}">${escapeHtml(h.code)}</button>
        `).join('')}
      </div>
      ${end < filtered.length ? `<button type="button" class="nav-btn house-more" data-tab-next aria-label="next">›</button>` : ''}
    </div>
  `;

  if (jumpFocused) {
    const again = nav.querySelector('[data-house-jump]');
    if (again) {
      again.focus();
      if (selStart != null) again.setSelectionRange(selStart, selEnd);
    }
  }
}

function shell(state, forceNav = false) {
  const lang = state.lang;
  const app = document.getElementById('app');
  const needShell = !app.querySelector('.topbar');
  if (needShell) {
    app.innerHTML = `
      <div class="demo-banner"><span class="dot"></span><span data-banner></span></div>
      <header class="topbar">
        <div class="brand"><span class="brand-mark">C</span><span data-brand></span></div>
        <div class="sep"></div>
        <span class="badge" data-house></span>
        <div class="spacer"></div>
        <span class="badge" data-role-badge></span>
        <span class="badge off" data-link-badge>未連線</span>
      </header>
      <nav class="nav house-nav" data-nav></nav>
      <main class="main home-all" id="main"></main>
      <footer class="status-footer">
        <span>Co-Make · greenhouse-control-app v0 · max ${MAX_HOUSES}</span>
        <span data-foot-mode></span>
        <span data-foot-alarms></span>
      </footer>
    `;
    const nav = app.querySelector('[data-nav]');
    nav.addEventListener('click', (ev) => {
      const addBtn = ev.target.closest('[data-house-add]');
      if (addBtn) {
        const st = getState();
        const code = window.prompt(t(st.lang, 'addHousePrompt'), `#${st.houseCount + 1}`);
        if (code == null) return;
        const r = addHouse({ code: code.trim() });
        if (!r.ok) {
          window.alert(reasonMessage(r.reason));
          return;
        }
        lastSig = '';
        lastNavSig = '';
        tabWindowStart = Math.max(0, getState().houseCount - TAB_WINDOW);
        paint(getState(), true);
        return;
      }
      if (ev.target.closest('[data-tab-prev]')) {
        tabWindowStart = Math.max(0, tabWindowStart - TAB_WINDOW);
        lastNavSig = '';
        paint(getState(), true);
        return;
      }
      if (ev.target.closest('[data-tab-next]')) {
        tabWindowStart += TAB_WINDOW;
        lastNavSig = '';
        paint(getState(), true);
        return;
      }
      const btn = ev.target.closest('[data-house-id]');
      if (!btn) return;
      const id = btn.dataset.houseId;
      if (id === getState().activeHouseId) return;
      destroyScene3d();
      setActiveHouse(id);
    });
    nav.addEventListener('input', (ev) => {
      const inp = ev.target.closest('[data-house-jump]');
      if (!inp) return;
      houseJumpFilter = inp.value;
      tabWindowStart = 0;
      lastNavSig = '';
      paint(getState(), true);
    });
    nav.addEventListener('keydown', (ev) => {
      const inp = ev.target.closest('[data-house-jump]');
      if (!inp || ev.key !== 'Enter') return;
      const q = houseJumpFilter.trim().toLowerCase();
      const list = getState().houseList;
      const hit = list.find((h) =>
        h.code.toLowerCase() === q
        || h.code.toLowerCase().includes(q)
        || h.name.toLowerCase().includes(q));
      if (hit) {
        destroyScene3d();
        setActiveHouse(hit.id);
        const idx = list.findIndex((h) => h.id === hit.id);
        tabWindowStart = Math.max(0, idx - Math.floor(TAB_WINDOW / 2));
        lastNavSig = '';
      }
    });
    forceNav = true;
  }

  app.querySelector('[data-banner]').textContent = t(lang, 'demoBanner');
  app.querySelector('[data-brand]').textContent = t(lang, 'appName');
  app.querySelector('[data-house]').textContent = `${state.houseCode} · ${state.houseName}`;
  app.querySelector('[data-role-badge]').textContent = state.role;
  app.querySelector('[data-foot-mode]').textContent = `${state.houseCode} · 模式 ${state.mode} · ${state.controlMode}`;
  app.querySelector('[data-foot-alarms]').textContent =
    `未清警報 ${state.alarms.filter((a) => !a.cleared).length}`;

  const ns = navSig(state);
  if (forceNav || ns !== lastNavSig) {
    renderHouseTabs(app.querySelector('[data-nav]'), state);
    lastNavSig = ns;
  }

  app.classList.toggle('viewer-mask', !canControlFrom(state));
}

function paintHome(main, state, ctx, force, changed) {
  const rebuilt = ensureHome(main, state.activeHouseId);
  const must = force || rebuilt || changed || state.activeHouseId !== lastActiveHouseId;

  const ov = main.querySelector('#section-overview');
  const env = main.querySelector('#section-env');
  const act = main.querySelector('#section-actuators');
  const water = main.querySelector('#section-water');
  const cam = main.querySelector('#section-cam');
  const s3 = main.querySelector('#section-scene3d');
  const set = main.querySelector('#section-settings');

  renderOverview(ov, state, ctx);
  renderEnvironment(env, state, ctx);

  if (must) {
    renderActuators(act, state, ctx);
    renderWater(water, state, ctx);
    renderCam(cam, state);
    renderSettings(set, state, ctx);
  }

  renderScene3d(s3, state, ctx);
  lastActiveHouseId = state.activeHouseId;
}

function paint(state, force = false) {
  shell(state, force);
  const main = document.getElementById('main');
  const ctx = { canControl: canControlFrom(state) };
  const sig = controlSig(state);
  const changed = force || sig !== lastSig;
  paintHome(main, state, ctx, force, changed);
  lastSig = sig;
}

function schedulePaint(state) {
  if (renderScheduled) return;
  renderScheduled = true;
  requestAnimationFrame(() => {
    renderScheduled = false;
    paint(state);
  });
}

startMockRuntime();
subscribe(schedulePaint);
paint(getState(), true);
